import React, { useCallback, useState, useEffect } from 'react'
import { DepositWayForm, DepositMethodType, Branch, DepositCategoryType } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import EditFinanceDepositWayForm, { EditFinanceDepositWayFormType, initialForm } from '../../../components/admin/finance/EditFinanceDepositWayForm'
import useT from '../../../i18ns/admin/useT'
import { useDialogHandleSubmit, useRedirectHandleBack, useGetDataByParams, useRequestFromSearch } from '../../../utils/default/ComplexFlowHook'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import depositTypeName from '../../../constants/default/depositTypeName'
import { formatMoney } from '../../../utils/default/TableHelper'
import allRoute, { Path } from '../../../components/admin/route/route'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { searchToRequest } from './FinanceDepositWayPage'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'
import { omitBy, isUndefined } from '@golden/utils'

const formToRequest: (form: EditFinanceDepositWayFormType) => DepositWayForm = (form) => {
  const converted = {
    forwarder_id: Number(form.courier),
    category: form.method[0] as DepositCategoryType,
    method: form.method[1] as DepositMethodType,
    group_id: form.group === '--' ? null : form.group,
    cash_min: Number(form.min),
    cash_max: Number(form.max),
    cash_full: Number(form.full),
    fee_rate: Number(form.userFee),
    layer_ids: form.playerLayer.layers,
    agent_types: form.agentLayers,
    is_active: form.isActive,
    is_recommended: form.isRecommended,
    clients: form.clients,
    is_for_proxy_deposit: form.playerLayer.isForProxyDeposit
  } as DepositWayForm
  return omitBy(converted, isUndefined) as DepositWayForm
}

const FinanceDepositWayUpdatePage: React.FC = () => {
  const request = useRequestFromSearch({ searchToRequest })
  const classes = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const pageFlow = usePageFlow()
  const [id, setId] = useState<number>(0)
  const [defaultForm, setDefaultForm] = useState<EditFinanceDepositWayFormType>(initialForm())
  const gdk = useGDK()
  const { t } = useT()
  const forwarders = useGDKStore.finance.forwarders()

  useEffect(() => {
    gdk.trunk.trigger([Branch.PLAYER_LAYER, Branch.FORWARDERS])
  }, [gdk])

  useGetDataByParams({
    path: Path.FINANCE_DEPOSIT_WAY_UPDATE,
    gdkFunc: (param: { id: string }) => gdk.finance.getDepositWay(Number(param.id)),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setId(res.id)
      setDefaultForm(initialForm({
        slug: res.slug,
        courier: res.forwarder_id,
        method: [res.category, res.method],
        min: formatMoney(res.cash_min),
        max: formatMoney(res.cash_max),
        group: res.group.id ?? '--',
        full: formatMoney(res.cash_full),
        userFee: formatMoney(res.fee_rate),
        playerLayer: {
          layers: res.layers.map((item) => item.id),
          isForProxyDeposit: res.is_for_proxy_deposit
        },
        agentLayers: res.agent_types,
        isActive: res.is_active,
        isRecommended: res.is_recommended,
        clients: res.clients
      }))
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: id === 0
  })

  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: allRoute.financeDepositWay.encodePath({ search: { ...request }, param: {} })
  })

  const { handleSubmit } = useDialogHandleSubmit<EditFinanceDepositWayFormType, DepositWayForm, null>({
    dialogId: 'updateWay',
    globalDialog,
    getChangeDialogConfig: useCallback((form) => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmUpdateDeposit')}
          <span className={classes.purpleWord}>
            {`${forwarders[form.slug]?.name ?? ''} ${t(depositTypeName[form.method[1] as DepositMethodType])}`}
          </span>
        </React.Fragment>
      )
    }), [t, forwarders, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updateDepositSucces')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    formToRequest,
    gdkFunc: (payload) => gdk.finance.updateDepositWay(id, payload),
    gdkFuncDependencies: [gdk, id],
    afterSuccessDialog: handleBack
  })

  return (
    <Box padding={5}>
      <LoadingAndErrorFrame { ...pageFlow.status } >
        <EditFinanceDepositWayForm
          title={t('common.updateDeposit')}
          okText={t('common.confirmUpdate')}
          onBack={handleDebouncedBack}
          onSubmit={handleSubmit}
          defaultValue={defaultForm}
          disableForwarder
        />
      </LoadingAndErrorFrame>
    </Box>
  )
}

export default React.memo(FinanceDepositWayUpdatePage)
